<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTimeAgo } from 'dashboard/composables/useTimeAgo';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['showDetail']);

const { t } = useI18n();
const { timeAgo } = useTimeAgo();

const ICONS = {
  activity_message: 'i-lucide-activity',
  note_added: 'i-lucide-sticky-note',
  audit_log: 'i-lucide-pencil',
  campaign_delivery: 'i-lucide-megaphone',
  flow_execution: 'i-lucide-workflow',
  kanban_event: 'i-lucide-square-kanban',
};

const TRACKING_ICONS = {
  page_viewed: 'i-lucide-globe',
  product_viewed: 'i-lucide-package',
  collection_viewed: 'i-lucide-layout-grid',
  search_submitted: 'i-lucide-search',
  cart_viewed: 'i-lucide-shopping-cart',
  product_added_to_cart: 'i-lucide-shopping-cart',
  product_removed_from_cart: 'i-lucide-shopping-cart',
  checkout_started: 'i-lucide-credit-card',
  order_completed: 'i-lucide-shopping-bag',
  customer_identified: 'i-lucide-user-check',
};

const icon = computed(() => {
  if (props.item.type === 'tracking_event') {
    return (
      TRACKING_ICONS[props.item.event_type] || 'i-lucide-mouse-pointer-click'
    );
  }
  return ICONS[props.item.type] || 'i-lucide-circle-dot';
});

const trackingTitle = item => {
  const payload = item.payload || {};
  const name = payload.product?.title || payload.collection?.title || '';
  const query = payload.search_query || '';
  switch (item.event_type) {
    case 'product_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_VIEWED', { name });
    case 'collection_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.COLLECTION_VIEWED', {
        name,
      });
    case 'search_submitted':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.SEARCH_SUBMITTED', {
        query,
      });
    case 'cart_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CART_VIEWED');
    case 'product_added_to_cart':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_ADDED_TO_CART', {
        name,
      });
    case 'product_removed_from_cart':
      return t(
        'CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_REMOVED_FROM_CART',
        {
          name,
        }
      );
    case 'checkout_started':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CHECKOUT_STARTED');
    case 'order_completed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.ORDER_COMPLETED');
    case 'customer_identified':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CUSTOMER_IDENTIFIED');
    default:
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PAGE_VIEWED');
  }
};

const title = computed(() => {
  const item = props.item;
  switch (item.type) {
    case 'flow_execution':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TYPES.FLOW_EXECUTION', {
        name: item.flow_name || '-',
        status: item.status,
      });
    case 'campaign_delivery':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TYPES.CAMPAIGN_DELIVERY', {
        name: item.campaign_name || '-',
        status: item.status,
      });
    case 'kanban_event':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TYPES.KANBAN_EVENT', {
        actor: item.actor?.name || t('CONTACTS_LAYOUT.ACTIVITIES.SYSTEM'),
        action: item.action,
        task: item.task_title || '-',
      });
    case 'note_added':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TYPES.NOTE_ADDED', {
        actor: item.actor?.name || t('CONTACTS_LAYOUT.ACTIVITIES.SYSTEM'),
      });
    case 'audit_log':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TYPES.AUDIT_LOG', {
        actor: item.actor?.name || t('CONTACTS_LAYOUT.ACTIVITIES.SYSTEM'),
        fields: (item.changed_keys || []).join(', '),
      });
    case 'tracking_event':
      return trackingTitle(item);
    default:
      return item.content || '';
  }
});

const time = computed(() => timeAgo(props.item.occurred_at));

const pageUrl = computed(() =>
  props.item.type === 'tracking_event' &&
  props.item.event_type === 'page_viewed'
    ? props.item.payload?.url || null
    : null
);
</script>

<template>
  <div class="flex items-start gap-2.5">
    <div
      class="flex items-center justify-center rounded-full size-6 bg-n-alpha-2 shrink-0"
    >
      <Icon :icon="icon" class="size-3.5 text-n-slate-11" />
    </div>
    <div class="flex-1 flex flex-col min-w-0 gap-0.5">
      <span class="text-sm break-words text-n-slate-12">{{ title }}</span>
      <a
        v-if="pageUrl"
        :href="pageUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs text-n-blue-10 truncate hover:underline"
      >
        {{ pageUrl }}
      </a>
      <span class="text-xs text-n-slate-10">{{ time }}</span>
    </div>
    <Button
      v-if="item.type === 'tracking_event'"
      icon="i-lucide-info"
      size="xs"
      variant="ghost"
      color="slate"
      class="shrink-0 mt-0.5"
      @click="emit('showDetail', item)"
    />
  </div>
</template>
